import {getKnowledgeData, useKnowledgeTranslation} from '../../i18n/knowledgeTranslation';
import React, {useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {ArrowRightOutlined, ClockCircleOutlined, BookOutlined} from '@ant-design/icons';
import {useTheme} from '../header/Header';
import Navigation from '../utils/Navigation';
import SeoHelmet from '../utils/seo';
import '../../css/knowledge-base.css';

function ArticleCard({article}) {
    const {english, t} = useKnowledgeTranslation();
    return (
        <article className="kb-card">
            <Link to={`${english ? "/en" : ""}/knowledge-base/${article.slug}`} className="kb-card-link">
                <div className="kb-card-topline">
                    <span className="kb-card-number">{article.topic}</span>
                    <span className="kb-card-category">{article.category}</span>
                </div>
                <h2>{article.title}</h2>
                <p>{article.excerpt}</p>
                <div className="kb-card-footer">
                    <span><ClockCircleOutlined/> {article.readingTime}</span>
                    <span className="kb-card-more">{t("Читать")}<ArrowRightOutlined/></span>
                </div>
            </Link>
        </article>
    );
}

export function KnowledgeBasePage() {
    const {english, t} = useKnowledgeTranslation();
    const {theme} = useTheme();
    const {articles, categories} = getKnowledgeData(english);
    const [category, setCategory] = useState(0);
    const filtered = useMemo(
        () => category === 0 ? articles : articles.filter(article => article.category === categories[category]),
        [category, articles, categories]
    );

    return (
        <>
            <SeoHelmet
                title={t("База знаний Сеспель — технологии и промышленное оборудование")}
                description={t("Профессиональные материалы Сеспель о промышленном оборудовании, СТП/FSW, станкостроении, СПГ и криогенной технике, силосах, контейнерах-цистернах и транспортной инфраструктуре.")}
                keywords={t("база знаний Сеспель, промышленное оборудование, СТП, FSW, станкостроение, СПГ, криогенная техника, силосы, контейнеры-цистерны")}
                canonicalUrl={english ? "/en/knowledge-base" : "/knowledge-base"}
            />
            <div className={`complexBack ${theme}`}> </div>
            <main className={`knowledge-base ${theme}`}>
                <Navigation/>
                <section className="kb-hero">
                    <div className="kb-eyebrow"><BookOutlined/>{t("БАЗА ЗНАНИЙ Сеспель")}</div>
                    <h1>{t("Инженерные материалы о технологиях и оборудовании")}</h1>
                    <p>{t("Разбираем устройство, принципы работы, критерии выбора и особенности эксплуатации промышленного оборудования понятным техническим языком.")}</p>
                    <div className="kb-hero-stats">
                        <div><strong>{articles.length}</strong><span>{t("материалов")}</span></div>
                        <div><strong>{categories.length - 1}</strong><span>{t("направлений")}</span></div>
                    </div>
                </section>

                <section className="kb-catalog" aria-labelledby="kb-list-title">
                    <div className="kb-toolbar">
                        <div>
                            <span className="kb-section-label">{t("МАТЕРИАЛЫ")}</span>
                            <h2 id="kb-list-title">{t("Выберите тему")}</h2>
                        </div>
                        <span className="kb-result-count">{filtered.length} {t(filtered.length === 1 ? 'статья' : 'статей')}</span>
                    </div>
                    <div className="kb-filters" role="group" aria-label={t("Фильтр по категориям")}>
                        {categories.map((item, index) => (
                            <button
                                key={item}
                                type="button"
                                className={category === index ? 'active' : ''}
                                aria-pressed={category === index}
                                onClick={() => setCategory(index)}
                            >
                                {item}
                                <span>{index === 0 ? articles.length : articles.filter(a => a.category === item).length}</span>
                            </button>
                        ))}
                    </div>
                    <div className="kb-grid">
                        {filtered.map(article => <ArticleCard key={article.slug} article={article}/>) }
                    </div>
                </section>
            </main>
        </>
    );
}

function ArticleChart({chart}) {
    const values = chart.items?.map(item => Number(item.value) || 0) || [];
    const maxValue = Math.max(...values, 1);

    return (
        <figure className="kb-chart" aria-label={chart.title}>
            <figcaption className="kb-chart-header">
                <strong>{chart.title}</strong>
                {chart.description ? <span>{chart.description}</span> : null}
            </figcaption>
            <div className="kb-chart-bars">
                {chart.items?.map(item => {
                    const value = Number(item.value) || 0;
                    const width = `${Math.max((value / maxValue) * 100, 2)}%`;
                    return (
                        <div className="kb-chart-row" key={item.label}>
                            <span className="kb-chart-label">{item.label}</span>
                            <div className="kb-chart-track" aria-hidden="true">
                                <span className="kb-chart-bar" style={{width}}/>
                            </div>
                            <strong className="kb-chart-value">{item.display ?? item.value}{chart.unit ? ` ${chart.unit}` : ''}</strong>
                        </div>
                    );
                })}
            </div>
            {chart.note ? <div className="kb-chart-note">{chart.note}</div> : null}
        </figure>
    );
}

function ArticleSection({section, onImageClick}) {
    const {t} = useKnowledgeTranslation();
    return (
        <section id={section.id} className="kb-article-section">
            <h2>{section.title}</h2>
            {section.paragraphs?.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
            {section.bullets?.length ? (
                <ul>{section.bullets.map((item, index) => <li key={index}>{item}</li>)}</ul>
            ) : null}
            {section.table ? (
                <div className="kb-table-wrap">
                    <table>
                        <thead><tr>{section.table.headers.map(header => <th key={header}>{header}</th>)}</tr></thead>
                        <tbody>{section.table.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>
                        ))}</tbody>
                    </table>
                </div>
            ) : null}
            {section.chart ? <ArticleChart chart={section.chart}/> : null}
            {section.media?.length ? (
                <div className={`kb-article-media ${section.media.length > 1 ? 'kb-article-media-grid' : ''}`}>
                    {section.media.map((item, index) => (
                        <figure key={`${item.src}-${index}`} className={item.variant === 'slide' ? 'kb-media-slide' : ''}>
                            <button
                                type="button"
                                className="kb-media-zoom"
                                onClick={() => onImageClick(item)}
                                aria-label={`${t('Увеличить изображение')}: ${item.alt || t('иллюстрация')}`}
                            >
                                <img src={item.src} alt={item.alt} loading="lazy"/>
                            </button>
                            {item.caption ? <figcaption>{item.caption}</figcaption> : null}
                        </figure>
                    ))}
                </div>
            ) : null}
            {section.note ? <div className="kb-note"><strong>{t("Важно.")}</strong> {section.note}</div> : null}
        </section>
    );
}

export function KnowledgeArticlePage() {
    const {english, t} = useKnowledgeTranslation();
    const {slug} = useParams();
    const {theme} = useTheme();
    const {articles} = getKnowledgeData(english);
    const article = articles.find(item => item.slug === slug);
    const [lightboxImage, setLightboxImage] = useState(null);

    useEffect(() => {
        setLightboxImage(null);
    }, [slug, english]);

    useEffect(() => {
        if (!lightboxImage) return undefined;

        const handleKeyDown = event => {
            if (event.key === 'Escape') setLightboxImage(null);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [lightboxImage]);

    if (!article) {
        return (
            <>
                <SeoHelmet title={t("Материал не найден")} description={t("Запрошенный материал базы знаний SESPEL не найден.")} canonicalUrl={english ? "/en/knowledge-base" : "/knowledge-base"}/>
                <div className={`complexBack ${theme}`}> </div>
                <main className={`knowledge-base kb-not-found ${theme}`}>
                    <Navigation/>
                    <h1>{t("Материал не найден")}</h1>
                    <p>{t("Возможно, ссылка устарела или статья была перемещена.")}</p>
                    <Link to={english ? "/en/knowledge-base" : "/knowledge-base"}>{t("← Вернуться в базу знаний")}</Link>
                </main>
            </>
        );
    }

    const related = articles
        .filter(item => item.slug !== article.slug && item.category === article.category)
        .slice(0, 3);
    const fallbackRelated = related.length ? related : articles.filter(item => item.slug !== article.slug).slice(0, 3);
    const articleUrl = `${english ? "/en" : ""}/knowledge-base/${article.slug}`;
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.seo.description,
        mainEntityOfPage: articleUrl,
        publisher: {'@type': 'Organization', name: 'SESPEL'},
        inLanguage: english ? 'en' : 'ru'
    };

    return (
        <>
            <SeoHelmet
                title={article.seo.title}
                description={article.seo.description}
                keywords={article.seo.keywords}
                canonicalUrl={articleUrl}
                jsonLd={jsonLd}
            />
            <div className={`complexBack ${theme}`}> </div>
            <main className={`knowledge-base kb-article-page ${theme}`}>
                <Navigation/>
                <article className="kb-article">
                    <Link to={english ? "/en/knowledge-base" : "/knowledge-base"} className="kb-back-link">{t("← Все материалы")}</Link>
                    <header className="kb-article-header">
                        <div className="kb-article-meta">
                            <span>{article.category}</span>
                            <span><ClockCircleOutlined/> {article.readingTime}</span>
                        </div>
                        <h1>{article.title}</h1>
                        <p className="kb-lead">{article.intro}</p>
                    </header>

                    <div className="kb-article-layout">
                        <aside className="kb-toc" aria-label={t("Содержание статьи")}>
                            <span>{t("Содержание")}</span>
                            <ol>
                                {article.sections.map(section => (
                                    <li key={section.id}><a href={`#${section.id}`}>{section.title}</a></li>
                                ))}
                            </ol>
                        </aside>
                        <div className="kb-article-content">
                            {article.sections.map(section => <ArticleSection key={section.id} section={section} onImageClick={setLightboxImage}/>) }

                            {article.sources?.length ? (
                                <section className="kb-sources">
                                    <h2>{t("Источники и технические материалы")}</h2>
                                    <ul>{article.sources.map((source, index) => <li key={index}>{source}</li>)}</ul>
                                    <p>{t("Конкретный набор нормативных требований определяется назначением изделия, отраслью, юрисдикцией и техническим заданием проекта.")}</p>
                                </section>
                            ) : null}

                            {article.relatedLinks?.length ? (
                                <section className="kb-product-links">
                                    <span className="kb-section-label">{t("ПО ТЕМЕ")}</span>
                                    <h2>{t("Продукция и решения Сеспель")}</h2>
                                    <div>
                                        {article.relatedLinks.map(link => (
                                            <Link key={link.url} to={english && /^\/(production|services)(?:\/|$)/.test(link.url) ? `/en${link.url}` : link.url}>{link.title}<ArrowRightOutlined/></Link>
                                        ))}
                                    </div>
                                </section>
                            ) : null}
                        </div>
                    </div>
                </article>

                <section className="kb-related">
                    <span className="kb-section-label">{t("ПРОДОЛЖИТЬ ЧТЕНИЕ")}</span>
                    <h2>{t("Другие материалы")}</h2>
                    <div className="kb-related-grid">
                        {fallbackRelated.map((item, index) => <ArticleCard key={item.slug} article={item} index={index}/>) }
                    </div>
                </section>
            </main>
            {lightboxImage ? (
                <div
                    className="kb-lightbox"
                    role="dialog"
                    aria-modal="true"
                    aria-label={lightboxImage.alt || t('Увеличенное изображение')}
                    onClick={() => setLightboxImage(null)}
                >
                    <button
                        type="button"
                        className="kb-lightbox-close"
                        onClick={() => setLightboxImage(null)}
                        aria-label={t("Закрыть увеличенное изображение")}
                    >
                        ×
                    </button>
                    <figure className="kb-lightbox-content" onClick={event => event.stopPropagation()}>
                        <img src={lightboxImage.src} alt={lightboxImage.alt || ''}/>
                        {lightboxImage.caption ? <figcaption>{lightboxImage.caption}</figcaption> : null}
                    </figure>
                </div>
            ) : null}
        </>
    );
}
